import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import env from 'config/env';
import { useEncryption } from './EncryptionContext';
import { JournalEntry } from '../types/journal';

interface JournalContextType {
  entries: JournalEntry[];
  isLoading: boolean;
  createEntry: (
    content: string,
    moodLabel?: string,
    entryDate?: string,
    location?: { lat: number; lng: number },
    weather?: {
      temperature: number;
      description: string;
      icon: string;
      humidity: number;
      windSpeed: number;
    },
    tags?: string[],
    customFields?: Array<{ id: string; value: any }>
  ) => Promise<{ id: string; entry: JournalEntry }>;
  updateEntry: (
    id: string,
    content: string,
    moodLabel?: string,
    entryDate?: string,
    location?: { lat: number; lng: number },
    weather?: {
      temperature: number;
      description: string;
      icon: string;
      humidity: number;
      windSpeed: number;
    },
    tags?: string[],
    customFields?: Array<{ id: string; value: any }>
  ) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  toggleFavorite: (id: string) => Promise<void>;
  refreshEntries: () => Promise<void>;
}

const JournalContext = createContext<JournalContextType | undefined>(undefined);

export function JournalProvider({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  const { encrypt, decrypt } = useEncryption();

  const refreshEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${env.API_URL}/journal`);
      
      // Decrypt content and mood labels for each entry
      const entriesWithDecrypted = await Promise.all(
        response.data.map(async (entry: JournalEntry) => ({
          ...entry,
          decrypted_content: await decrypt(entry.encrypted_content),
          decrypted_mood_label: entry.encrypted_mood_label ? await decrypt(entry.encrypted_mood_label) : undefined
        }))
      );
      
      setEntries(entriesWithDecrypted);
    } catch (error) {
      toast({
        title: 'Error fetching entries',
        description: 'Could not load journal entries',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, decrypt]);

  useEffect(() => {
    refreshEntries();
  }, [refreshEntries]);

  const createEntry = useCallback(async (
    content: string,
    moodLabel?: string,
    entryDate?: string,
    location?: { lat: number; lng: number },
    weather?: {
      temperature: number;
      description: string;
      icon: string;
      humidity: number;
      windSpeed: number;
    },
    tags?: string[],
    customFields?: Array<{ id: string; value: any }>
  ) => {
    try {
      // First get the embedding from the raw content
      const embeddingResponse = await axios.post(`${env.API_URL}/ai/embeddings`, {
        text: content
      });
      const vector = embeddingResponse.data.embedding;

      // Encrypt content, vector and mood label
      const encrypted_content = await encrypt(content);
      const encrypted_vector = await encrypt(JSON.stringify(vector));
      const encrypted_mood_label = moodLabel ? await encrypt(moodLabel) : null;
      
      // Encrypt custom fields
      const encrypted_custom_fields = await Promise.all(
        (customFields || []).map(async field => ({
          id: field.id,
          value: await encrypt(field.value.toString())
        }))
      );

      const response = await axios.post(`${env.API_URL}/journal`, {
        encrypted_content,
        encrypted_vector,
        encrypted_mood_label,
        entry_date: entryDate,
        location_lat: location?.lat,
        location_lng: location?.lng,
        weather_data: weather,
        tags: tags || [],
        custom_fields: encrypted_custom_fields
      });

      const newEntry = {
        ...response.data,
        decrypted_content: content,
        decrypted_mood_label: moodLabel
      };
      setEntries(prevEntries => [newEntry, ...prevEntries]);
      return { id: newEntry.id, entry: newEntry };
    } catch (error) {
      toast({
        title: 'Error creating entry',
        description: 'Could not create journal entry',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  }, [encrypt, toast]);

  const updateEntry = useCallback(async (
    id: string,
    content: string,
    moodLabel?: string,
    entryDate?: string,
    location?: { lat: number; lng: number },
    weather?: {
      temperature: number;
      description: string;
      icon: string;
      humidity: number;
      windSpeed: number;
    },
    tags?: string[],
    customFields?: Array<{ id: string; value: any }>
  ) => {
    try {
      // First get the embedding from the raw content
      const embeddingResponse = await axios.post(`${env.API_URL}/ai/embeddings`, {
        text: content
      });
      const vector = embeddingResponse.data.embedding;

      // Encrypt content, vector and mood label
      const encrypted_content = await encrypt(content);
      const encrypted_vector = await encrypt(JSON.stringify(vector));
      const encrypted_mood_label = moodLabel ? await encrypt(moodLabel) : null;
      
      // Encrypt custom fields
      const encrypted_custom_fields = await Promise.all(
        (customFields || []).map(async field => ({
          id: field.id,
          value: await encrypt(field.value.toString())
        }))
      );

      const response = await axios.put(`${env.API_URL}/journal/${id}`, {
        encrypted_content,
        encrypted_vector,
        encrypted_mood_label,
        entry_date: entryDate,
        location_lat: location?.lat,
        location_lng: location?.lng,
        weather_data: weather,
        tags: tags || [],
        custom_fields: encrypted_custom_fields
      });

      // Decrypt content and mood labels for each entry
      const entriesWithDecrypted = await Promise.all(
        response.data.map(async (entry: JournalEntry) => ({
          ...entry,
          decrypted_content: await decrypt(entry.encrypted_content),
          decrypted_mood_label: entry.encrypted_mood_label ? await decrypt(entry.encrypted_mood_label) : undefined
        }))
      );
      
      setEntries(entriesWithDecrypted);
    } catch (error) {
      toast({
        title: 'Error updating entry',
        description: 'Could not update journal entry',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  }, [encrypt, decrypt, toast]);

  const deleteEntry = useCallback(async (id: string) => {
    try {
      await axios.delete(`${env.API_URL}/journal/${id}`);
      setEntries(prev => prev.filter(entry => entry.id !== id));
    } catch (error) {
      toast({
        title: 'Error deleting entry',
        description: 'Could not delete journal entry',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  const toggleFavorite = useCallback(async (id: string) => {
    try {
      const entry = entries.find(e => e.id === id);
      if (!entry) return;

      const response = await axios.put(`${env.API_URL}/journal/${id}/favorite`, {
        is_favorite: !entry.is_favorite,
      });

      setEntries(prev => prev.map(e => 
        e.id === id ? { ...e, is_favorite: !e.is_favorite } : e
      ));
    } catch (error) {
      toast({
        title: 'Error updating favorite',
        description: 'Could not update favorite status',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [entries, toast]);

  return (
    <JournalContext.Provider
      value={{
        entries,
        isLoading,
        createEntry,
        updateEntry,
        deleteEntry,
        toggleFavorite,
        refreshEntries,
      }}
    >
      {children}
    </JournalContext.Provider>
  );
}

export function useJournal() {
  const context = useContext(JournalContext);
  if (context === undefined) {
    throw new Error('useJournal must be used within a JournalProvider');
  }
  return context;
} 